import React, { useRef, useEffect } from 'react';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
}

/**
 * KERN Dialog Component
 *
 * Usage:
 * <Dialog
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   title="Bestätigung"
 *   footer={
 *     <>
 *       <Button variant="secondary" onClick={onCancel}>Abbrechen</Button>
 *       <Button variant="primary" onClick={onConfirm}>Speichern</Button>
 *     </>
 *   }
 * >
 *   Möchten Sie die Änderungen speichern?
 * </Dialog>
 */
export function Dialog({ open, onClose, title, children, footer, className = '' }: DialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open) {
      dialog.showModal();
    } else {
      dialog.close();
    }
  }, [open]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleClose = () => onClose();
    dialog.addEventListener('close', handleClose);

    return () => dialog.removeEventListener('close', handleClose);
  }, [onClose]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === dialogRef.current) {
      onClose();
    }
  };

  const classes = ['kern-dialog', className].filter(Boolean).join(' ');
  const headingId = `dialog-heading-${title.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <dialog
      ref={dialogRef}
      className={classes}
      aria-labelledby={headingId}
      onClick={handleBackdropClick}
    >
      <header className="kern-dialog__header">
        <h2 className="kern-title kern-title--large" id={headingId}>
          {title}
        </h2>
        <button className="kern-btn kern-btn--tertiary" onClick={onClose}>
          <span className="kern-icon kern-icon--close" aria-hidden="true" />
          <span className="kern-sr-only">Schließen</span>
        </button>
      </header>
      <section className="kern-dialog__body">
        {typeof children === 'string' ? <p className="kern-body">{children}</p> : children}
      </section>
      {footer && <footer className="kern-dialog__footer">{footer}</footer>}
    </dialog>
  );
}

export default Dialog;
